import { gql } from '@apollo/client';

const ASSET_STATE_PARTS = gql`
  fragment AssetStateParts on AssetState {
    id
    time
    stateOfCharge
    instruction
    frequency
    powerOutput
    voltage
    temperature
  }
`;

const SITE_STATE_PARTS = gql`
  fragment SiteStateParts on SiteState {
    id
    time
    stateOfCharge
    frequency
    powerOutput
    instruction
  }
`;

const SITE_PARTS = gql`
  fragment SiteParts on Site {
    totalLoadRating
    totalEnergyCapacity
    roundtripEfficiency
    maxCyclesPerDay
    maxCyclesPerYear
    chargeRate
    dischargeRate
    maxSOCLimit
    minSOCLimit
  }
`;

const SITE_ASSET_ALERTS_PARTS = gql`
  fragment SiteAssetAlertParts on Site {
    assets {
      edges {
        node {
          id
          name
          current {
            state {
              stateOfCharge
              powerOutput
              instruction
            }
            alerts {
              id
              time
              alertType {
                key
                description
                id
                label
                severity
              }
            }
            mode {
              id
              modeType {
                id
                label
                vppNumber
                vppStatus
                description
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_ASSET_OPERATIONS_SUMMARY = gql`
  query GetAssetOperationsSummary(
    $assetId: ID!
    $start: DateTime!
    $end: DateTime!
  ) {
    asset(id: $assetId) {
      id
      totalLoadRating
      installedCapacity
      stateSummary(start: $start, end: $end) {
        import
        export
        cycles
      }
      site {
        id
        maxSOCLimit
        minSOCLimit
      }
    }
  }
`;

export const GET_ASSET_OPERATIONS_STATES = gql`
  ${ASSET_STATE_PARTS}
  query GetAssetOperationsStates(
    $assetId: ID!
    $start: DateTime!
    $end: DateTime!
  ) {
    asset(id: $assetId) {
      id
      states(start: $start, end: $end) {
        ...AssetStateParts
      }
    }
  }
`;

export const FETCH_MORE_ASSET_OPERATIONS_STATES = gql`
  ${ASSET_STATE_PARTS}
  query FetchMoreAssetOperationsStates(
    $assetId: ID!
    $start: DateTime!
    $end: DateTime!
  ) {
    asset(id: $assetId) {
      id
      states(start: $start, end: $end) {
        ...AssetStateParts
      }
    }
  }
`;

export const CREATED_ASSET_STATE = gql`
  ${ASSET_STATE_PARTS}
  subscription CreatedAssetState($assetId: Float!) {
    createdAssetState(assetId: $assetId) {
      __typename
      ...AssetStateParts
    }
  }
`;

export const GET_SITE_OPERATIONS_SUMMARY = gql`
  ${SITE_PARTS}
  query GetSiteOperationsSummary(
    $siteId: ID!
    $start: DateTime!
    $end: DateTime!
  ) {
    site(id: $siteId) {
      id
      capacity
      stateSummary(start: $start, end: $end) {
        import
        export
        cycles
      }
      installedCapacity
      ...SiteParts
    }
  }
`;

export const GET_SITE_OPERATIONS_STATES = gql`
  ${SITE_STATE_PARTS}
  query GetSiteOperationsStates(
    $siteId: ID!
    $start: DateTime!
    $end: DateTime!
  ) {
    site(id: $siteId) {
      id
      states(start: $start, end: $end) {
        ...SiteStateParts
      }
    }
  }
`;

export const FETCH_MORE_SITE_OPERATIONS_STATES = gql`
  ${SITE_STATE_PARTS}
  query FetchMoreSiteOperationsStates(
    $siteId: ID!
    $start: DateTime!
    $end: DateTime!
  ) {
    site(id: $siteId) {
      id
      states(start: $start, end: $end) {
        ...SiteStateParts
      }
    }
  }
`;

export const CREATED_SITE_STATE = gql`
  ${SITE_STATE_PARTS}
  subscription CreatedSiteState($siteId: Float!) {
    createdSiteState(siteId: $siteId) {
      __typename
      ...SiteStateParts
    }
  }
`;

export const GET_SITES = gql`
  ${SITE_ASSET_ALERTS_PARTS}
  query GetCompanySites($companyId: ID!) {
    company(id: $companyId) {
      id
      sites {
        id
        name
        current {
          state {
            id
            stateOfCharge
            powerOutput
            instruction
          }
          mode {
            id
            modeType {
              id
              label
              vppNumber
              vppStatus
              description
            }
          }
        }
        ...SiteAssetAlertParts
      }
    }
  }
`;
